import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { AppService } from '../app.service';
import { SharedWorkspaceService } from '../services/shared-workspace.service';

/**
 * Where the emailed invitation link lands: /invite?token=...
 *
 * The token names which invitation is meant; it is not what authorises the
 * join. The server checks that the signed-in address matches the invited one,
 * so this page needs a session before it can show anything - and someone who
 * signs in can also find the same invitation waiting on the shared workspaces
 * page, without any link at all.
 */
@Component({
  selector: 'app-accept-invite',
  standalone: false,
  templateUrl: './accept-invite.component.html',
  styleUrls: ['./accept-invite.component.css'],
})
export class AcceptInviteComponent implements OnInit {

  token = '';
  invite: any = null;
  signedIn = false;
  loading = true;
  busy = false;
  error = '';

  constructor(
    private route: ActivatedRoute,
    private workspaces: SharedWorkspaceService,
    private app: AppService,
    private toastr: ToastrService,
    public router: Router,
  ) { }

  async ngOnInit(): Promise<void> {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    this.signedIn = !!this.app.get('xdbc.accessToken');

    if (!this.token) {
      this.error = 'This link is missing its invitation.';
      this.loading = false;
      return;
    }
    if (!this.signedIn) {
      this.loading = false;
      return;
    }

    try {
      this.invite = await this.workspaces.viewInvite({ token: this.token });
    } catch (error: any) {
      // Deliberately the same message whether the invitation never existed or
      // belongs to somebody else - the server does not distinguish them either.
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  }

  signIn(): void {
    this.router.navigate(['/login']);
  }

  async accept(): Promise<void> {
    this.busy = true;
    try {
      const joined = await this.workspaces.acceptInvite({ token: this.token });
      this.toastr.success(`You can now read ${joined.workspaceName}.`);
      this.router.navigate(['/shared-workspace', joined.workspaceId]);
    } catch (error: any) {
      this.toastr.error(error.message);
      this.error = error.message;
    } finally {
      this.busy = false;
    }
  }

  async decline(): Promise<void> {
    this.busy = true;
    try {
      await this.workspaces.declineInvite({ token: this.token });
      this.toastr.info('Invitation declined.');
      this.router.navigate(['/shared-workspaces']);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }
}
